import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { patch, type Ingest, type StreamKey, type StreamKeyPatch } from '../api'
import { t } from '../i18n'
import { graceSaveError, graceText } from './grace'
import { parseSeconds, SLATE_AFTER_DEFAULT_S, SLATE_AFTER_MAX_S, SLATE_AFTER_MIN_S, SLATE_MAX_MAX_S, SLATE_MAX_MIN_S } from './slate'
import { Field, PrimaryButton, SecondaryButton, Switch } from './ui'

/**
 * The stream key's break screen (SLATE-1) next to "Hold when OBS drops": a row with
 * what is in force that opens the setting; Save sends PATCH /api/stream-keys/{id}
 * {slate_enabled, slate_after_ms, slate_max_ms} (null = the default).
 */
export function KeySlate({ streamKey, ingest }: { streamKey: StreamKey; ingest?: Ingest }) {
  const qc = useQueryClient()
  const on = streamKey.slate_enabled ?? true
  const after = streamKey.slate_after_ms ?? null
  const max = streamKey.slate_max_ms ?? null
  const hold = streamKey.source_grace_ms ?? ingest?.default_source_grace_ms
  const [open, setOpen] = useState(false)
  const [vOn, setOn] = useState(on)
  const [vAfter, setAfter] = useState('')
  const [vMax, setMax] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(false)
  const revoked = !!streamKey.revoked_at
  const a = parseSeconds(vAfter, SLATE_AFTER_MIN_S, SLATE_AFTER_MAX_S)
  const m = parseSeconds(vMax, SLATE_MAX_MIN_S, SLATE_MAX_MAX_S)
  const dirty = vOn !== on || a.ms !== after || m.ms !== max
  const invalid = !!a.bad || !!m.bad
  const holdText = hold === undefined ? t('slate.holdDefault') : graceText(hold)

  const toggle = () => {
    if (!open) {
      setOn(on)
      setAfter(after === null ? '' : String(after / 1000))
      setMax(max === null ? '' : String(max / 1000))
      setErr('')
    }
    setDone(false)
    setOpen((o) => !o)
  }

  const save = async () => {
    if (!dirty || invalid || busy) return
    setBusy(true)
    setErr('')
    try {
      const body: StreamKeyPatch = { slate_enabled: vOn, slate_after_ms: a.ms ?? null, slate_max_ms: m.ms ?? null }
      await patch<StreamKey>(`/api/stream-keys/${streamKey.id}`, body)
      await qc.invalidateQueries({ queryKey: ['stream-keys'] })
      setOpen(false)
      setDone(true)
    } catch (e) {
      setErr(graceSaveError(e))
      void qc.invalidateQueries({ queryKey: ['stream-keys'] })
    } finally {
      setBusy(false)
    }
  }

  const now = on ? t('slate.nowOn', { s: (after ?? SLATE_AFTER_DEFAULT_S * 1000) / 1000 }) : t('slate.nowOff')
  return (
    <div className="flex flex-col gap-2" data-testid="key-slate-block">
      <button type="button" data-testid="key-slate-toggle" aria-expanded={open} onClick={toggle}
        className="flex min-h-[44px] items-center justify-between gap-2 rounded-md border border-line-2 px-2.5 text-left text-[13px] text-text-soft hover:text-text md:min-h-9">
        <span>{t('slate.row')}</span>
        <span className="tabular font-mono text-xs text-muted" data-testid="key-slate-now">{now} {open ? '▴' : '▾'}</span>
      </button>
      {done && !open && <p role="status" data-testid="key-slate-saved" className="text-xs text-live">{t('slate.saved')}</p>}
      {open && (
        <div className="flex flex-col gap-2.5">
          {revoked ? (
            <p className="text-xs text-fail">{t('slate.revoked')}</p>
          ) : (
            <>
              <Switch label={t('slate.switch')} on={vOn} onChange={(v) => { setOn(v); setErr('') }} sub={t('slate.hint')} />
              {vOn && (
                <div className="grid grid-cols-2 gap-2 max-[389px]:grid-cols-1">
                  <Field label={t('slate.after')} value={vAfter} placeholder={String(SLATE_AFTER_DEFAULT_S)} mono name="slate-after"
                    error={a.bad ? t('slate.range', { min: SLATE_AFTER_MIN_S, max: SLATE_AFTER_MAX_S }) : undefined} hint={t('slate.afterHint')} disabled={busy} onChange={(v) => { setAfter(v); setErr('') }} />
                  <Field label={t('slate.max')} value={vMax} placeholder={t('slate.maxPlaceholder')} mono name="slate-max"
                    error={m.bad ? t('slate.range', { min: SLATE_MAX_MIN_S, max: SLATE_MAX_MAX_S }) : undefined} hint={t('slate.maxHint', { hold: holdText })} disabled={busy} onChange={(v) => { setMax(v); setErr('') }} />
                </div>
              )}
            </>
          )}
          {err && <p role="alert" data-testid="key-slate-error" className="text-xs text-fail">{err}</p>}
          <div className="grid grid-cols-2 gap-2">
            <SecondaryButton className="max-md:min-h-[44px]" disabled={busy} onClick={toggle}>{t('slate.cancel')}</SecondaryButton>
            <PrimaryButton className="max-md:min-h-[44px]" testId="key-slate-save" busy={busy} disabled={revoked || !dirty || invalid} onClick={() => void save()}>
              {busy ? t('slate.saving') : err ? t('slate.retry') : t('slate.save')}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  )
}
